import React from "react";

/**
 * OitSummaryLoading
 * React ^16.3.2 compatible (no hooks)
 *
 * Props:
 * - isLoading: boolean (default true)
 * - messages: string[] (optional; defaults provided)
 * - intervalMs: number (default 1800)
 * - loop: boolean (default true)
 * - onDone: function (optional; called when reaches last message and loop=false)
 * - className: string (optional)
 */
export default class OitSummaryLoading extends React.Component {
  constructor(props) {
    super(props);

    this.defaultMessages = [
      "Connecting to reporting engine…",
      "Pulling trainee exposure and call types…",
      "Summarizing current phase performance trends…",
      "Identifying high-liability experience gaps…",
      "Extracting documented 1–2 performance exceptions…",
      "Reviewing task-file progress vs program phase…",
      "Building Chief one-page dashboard JSON…",
      "Finalizing visuals and sections…",
    ];

    this.state = { index: 0 };
    this._timer = null;
  }

  componentDidMount() {
    if (this.getIsLoading()) this.start();
  }

  componentDidUpdate(prevProps) {
    var wasLoading = prevProps && prevProps.isLoading !== undefined ? prevProps.isLoading : true;
    var isLoading = this.getIsLoading();

    // If loading started, reset and start cycling
    if (!wasLoading && isLoading) {
      this.setState({ index: 0 });
      this.start();
    }

    // If loading stopped, stop cycling
    if (wasLoading && !isLoading) {
      this.stop();
    }

    // If interval/messages changed while loading, restart timer
    var prevInterval = prevProps.intervalMs;
    var nextInterval = this.props.intervalMs;
    var prevMsgs = prevProps.messages;
    var nextMsgs = this.props.messages;

    if (isLoading && (prevInterval !== nextInterval || prevMsgs !== nextMsgs)) {
      this.stop();
      this.start();
    }
  }

  componentWillUnmount() {
    this.stop();
  }

  getIsLoading() {
    return this.props.isLoading === undefined ? true : !!this.props.isLoading;
  }

  getMessages() {
    var msgs = this.props.messages;
    return Array.isArray(msgs) && msgs.length ? msgs : this.defaultMessages;
  }

  start() {
    var _this = this;
    if (this._timer) return;

    var intervalMs = typeof this.props.intervalMs === "number" ? this.props.intervalMs : 1800;

    this._timer = setInterval(function () {
      var msgs = _this.getMessages();
      if (!msgs.length) return;

      _this.setState(function (prevState) {
        var next = prevState.index + 1;

        // End behavior
        var loop = _this.props.loop === undefined ? true : !!_this.props.loop;
        if (next >= msgs.length) {
          if (loop) {
            next = 0;
          } else {
            next = msgs.length - 1;
            _this.stop();
            if (typeof _this.props.onDone === "function") _this.props.onDone();
          }
        }
        return { index: next };
      });
    }, intervalMs);
  }

  stop() {
    if (this._timer) {
      clearInterval(this._timer);
      this._timer = null;
    }
  }

  render() {
    var msgs = this.getMessages();
    var idx = this.state.index;
    var message = msgs[idx] || "Working…";

    // simple styles, can be overridden via className
    return (
      <div
        className={this.props.className}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: 14,
          background: "rgb(31 41 89)",
          color: "#fff",
          boxShadow: "0 10px 24px rgba(0,0,0,0.18)",
          maxWidth: '720px',
          margin: '50px auto',
          borderRadius: '14px'
        }}
        role="status"
        aria-live="polite"
        aria-busy={this.getIsLoading()}
      >
        <Spinner />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 12, fontWeight: 800, opacity: 0.85, letterSpacing: 0.2 }}>
            Generating report
          </div>
          <div
            style={{
              fontSize: 14,
              fontWeight: 800,
              marginTop: 2,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
            title={message}
          >
            {message}
          </div>
        </div>
      </div>
    );
  }
}

function Spinner() {
  // inline CSS animation (no external stylesheet required)
  var ringStyle = {
    width: 22,
    height: 22,
    borderRadius: "50%",
    border: "3px solid rgba(255,255,255,0.25)",
    borderTopColor: "#ffffff",
    animation: "aiSpin 0.9s linear infinite",
    flex: "0 0 auto",
  };

  return (
    <React.Fragment>
      <span style={ringStyle} aria-hidden="true" />
      <style>
        {`
          @keyframes aiSpin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </React.Fragment>
  );
}
